import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Btn } from "../ui";

export type TourStep = {
  /** Element per CSS-Selektor hervorheben. */
  selector?: string;
  /** Alternativ: sichtbaren Button/Link per Text finden (Inhaltsbereich, dann Dialoge).
   *  Mehrere Texte = Alternativen in dieser Reihenfolge (z. B. je nach Zustand). */
  findText?: string | string[];
  /** Alternativ: Eingabefeld über Beschriftung, Platzhalter oder aria-label finden. */
  findLabel?: string;
  /** Ohne selector/findText/findLabel: zentrierter Hinweis. */
  title: string;
  body: string;
  /** Vor dem Anzeigen in diesen Tab navigieren (via onNavigate). */
  tab?: string;
  /**
   * Mitmach-Schritt: das markierte Element bleibt bedienbar.
   * - "click": der Klick auf das Element führt automatisch zum nächsten Schritt.
   * - "input": das Feld kann ausgefüllt werden; weiter mit „Weiter“.
   */
  action?: "click" | "input";
  /** Bei action "input": „Weiter“ erst, wenn das Feld nicht leer ist. */
  requireValue?: boolean;
  /** Wartezeit (ms), bis das Ziel erscheint. Standard 3000. (waitForText wartet separat bis 60 s.) */
  timeout?: number;
  /** Schritt automatisch überspringen, wenn das Ziel nicht gefunden wird. */
  optional?: boolean;
  /** Suche nach findText/findLabel auf diesen Bereich (CSS-Selektor) beschränken. */
  within?: string;
  /** findText nur bei exakt gleichem Text (z. B. „Neu“, nicht „Neue …“). */
  exact?: boolean;
  /** Vor dem Anzeigen warten, bis dieser Text irgendwo erscheint (z. B. Erfolgsmeldung). */
  waitForText?: string;
};

type Rect = { top: number; left: number; width: number; height: number };

const normalize = (value: string) => value.replace(/\s+/g, " ").trim().toLowerCase();

const isVisible = (el: HTMLElement) =>
  el.offsetParent !== null || getComputedStyle(el).position === "fixed";

const notInOverlay = (el: Element) => !el.closest("[data-guided-tour]");

// Sucht zuerst im Inhaltsbereich, danach im ganzen Dokument (Dialoge liegen als
// Portal außerhalb von <main>). Das Tour-Overlay selbst wird ignoriert.
function scopes(within?: string): ParentNode[] {
  if (within) {
    const el = document.querySelector<HTMLElement>(within);
    return el ? [el] : [];
  }
  const main = document.querySelector<HTMLElement>('main[role="main"]');
  return main ? [main, document] : [document];
}

// Findet einen sichtbaren Button/Link/Tab anhand des Textes.
function findByText(text: string, within?: string, exact?: boolean): HTMLElement | null {
  const target = normalize(text);
  for (const scope of scopes(within)) {
    const candidates = Array.from(
      scope.querySelectorAll<HTMLElement>('button, a, [role="button"], [role="tab"], [role="menuitem"], summary')
    ).filter((el) => isVisible(el) && notInOverlay(el));
    const hit =
      candidates.find((el) => normalize(el.textContent || "") === target) ||
      (exact ? undefined : candidates.find((el) => normalize(el.textContent || "").includes(target)));
    if (hit) return hit;
  }
  return null;
}

// Findet ein Eingabefeld über Platzhalter, aria-label, name oder zugehöriges <label>.
function findByLabel(text: string, within?: string): HTMLElement | null {
  const target = normalize(text);
  const fields = (scope: ParentNode) =>
    Array.from(scope.querySelectorAll<HTMLElement>("input, textarea, select, [contenteditable='true']"))
      .filter((el) => isVisible(el) && notInOverlay(el) && (el as HTMLInputElement).type !== "hidden");
  const attrText = (el: HTMLElement) =>
    normalize([el.getAttribute("placeholder"), el.getAttribute("aria-label"), el.getAttribute("name")].filter(Boolean).join(" "));
  for (const scope of scopes(within)) {
    const list = fields(scope);
    const direct = list.find((el) => attrText(el).includes(target));
    if (direct) return direct;
    const labels = Array.from(scope.querySelectorAll<HTMLLabelElement>("label")).filter(
      (l) => notInOverlay(l) && normalize(l.textContent || "").includes(target)
    );
    for (const label of labels) {
      const byFor = label.htmlFor ? document.getElementById(label.htmlFor) : null;
      if (byFor && isVisible(byFor)) return byFor;
      const inner = label.querySelector<HTMLElement>("input, textarea, select");
      if (inner && isVisible(inner)) return inner;
      // Häufiges Muster: <label>Text</label><input/> im selben Container.
      const sibling = label.parentElement?.querySelector<HTMLElement>("input, textarea, select");
      if (sibling && isVisible(sibling)) return sibling;
    }
    // Labeled-Muster (<div><span>Titel</span><input/></div>): kurzes Textelement mit
    // exakt (oder beginnend mit) dem Beschriftungstext, Feld im selben Block.
    const textEls = Array.from(scope.querySelectorAll<HTMLElement>("span, div, p, legend, h4, h5"))
      .filter((el) => notInOverlay(el) && el.children.length === 0 && isVisible(el));
    const exactHits = textEls.filter((el) => normalize(el.textContent || "") === target);
    const startHits = textEls.filter((el) => normalize(el.textContent || "").startsWith(target));
    for (const el of [...exactHits, ...startHits]) {
      const field = el.parentElement?.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select");
      if (field && isVisible(field)) return field;
    }
  }
  return null;
}

function resolveTarget(step: TourStep): HTMLElement | null {
  if (step.selector) {
    const el = document.querySelector<HTMLElement>(step.selector);
    return el && notInOverlay(el) ? el : null;
  }
  if (step.findText) {
    for (const text of Array.isArray(step.findText) ? step.findText : [step.findText]) {
      const hit = findByText(text, step.within, step.exact);
      if (hit) return hit;
    }
    return null;
  }
  if (step.findLabel) return findByLabel(step.findLabel, step.within);
  return null;
}

const hasTarget = (step: TourStep) => Boolean(step.selector || step.findText || step.findLabel);

// Wartet, bis ein Text irgendwo im Dokument erscheint (z. B. Erfolgsmeldung).
function waitForText(text: string, timeout: number): Promise<boolean> {
  const has = () => (document.body.textContent || "").includes(text);
  if (has()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let elapsed = 0;
    const id = window.setInterval(() => {
      elapsed += 150;
      if (has() || elapsed >= timeout) {
        window.clearInterval(id);
        resolve(has());
      }
    }, 150);
  });
}

// Wartet, bis das Ziel des Schritts im DOM auftaucht (nach Navigation/Lazy-Load/Speichern).
function waitForTarget(step: TourStep): Promise<HTMLElement | null> {
  const timeout = step.timeout ?? 3000;
  const existing = resolveTarget(step);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    let elapsed = 0;
    const stepMs = 80;
    const id = window.setInterval(() => {
      const el = resolveTarget(step);
      elapsed += stepMs;
      if (el || elapsed >= timeout) {
        window.clearInterval(id);
        resolve(el);
      }
    }, stepMs);
  });
}

const fieldValue = (el: HTMLElement | null) => {
  if (!el) return "";
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return el.value;
  return el.textContent || "";
};

const TOOLTIP_WIDTH = 340;
const PAD = 6;

/**
 * Geführte Tour / Mitmach-Tutorial.
 * onFinish(true) = bis zum letzten Schritt durchlaufen, onFinish(false) = abgebrochen.
 */
export function GuidedTour({ steps, onFinish, onNavigate }: {
  steps: TourStep[];
  onFinish: (completed?: boolean) => void;
  onNavigate: (tab: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);
  // Gefundenes Ziel zusätzlich als State: React bündelt setReady(false)/setReady(true)
  // beim Schrittwechsel, daher würden Effekte mit [ready, index] das Ziel verpassen.
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const step = steps[index];
  const isLast = index === steps.length - 1;
  const interactive = Boolean(step?.action) && Boolean(rect);

  const goNext = () => (isLast ? onFinish(true) : setIndex((value) => Math.min(value + 1, steps.length - 1)));

  // Aktuellen Schritt vorbereiten: navigieren, Ziel finden, Position bestimmen.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setRect(null);
    setHasValue(false);
    targetRef.current = null;
    setTargetEl(null);
    (async () => {
      if (step.tab) onNavigate(step.tab);
      // Auf Rückmeldungen (z. B. nach dem Speichern) großzügig warten – unabhängig von step.timeout.
      if (step.waitForText) await waitForText(step.waitForText, 60000);
      if (cancelled) return;
      if (!hasTarget(step)) {
        if (!cancelled) setReady(true);
        return;
      }
      const el = await waitForTarget(step);
      if (cancelled) return;
      if (!el && step.optional) {
        goNext();
        return;
      }
      if (el) {
        el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
        targetRef.current = el;
        setTargetEl(el);
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        if (step.action === "input") {
          window.setTimeout(() => el.focus({ preventScroll: true }), 250);
          setHasValue(fieldValue(el).trim().length > 0);
        }
      }
      setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Markierung folgt dem Element (Scroll, Resize, aufklappende Dialoge, Layout-Wechsel).
  useEffect(() => {
    if (!ready) return;
    const measure = () => {
      let el = targetRef.current;
      if (!el) return;
      if (!el.isConnected) {
        // Element wurde neu gerendert – erneut suchen.
        const again = resolveTarget(step);
        if (!again) return;
        el = again;
        targetRef.current = again;
        setTargetEl(again);
      }
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5 &&
        Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height }
      );
      if (step.action === "input") setHasValue(fieldValue(el).trim().length > 0);
    };
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const id = window.setInterval(measure, 250);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, index]);

  // Mitmach-Schritt "click": Klick auf das markierte Element führt weiter.
  useEffect(() => {
    if (!ready || step.action !== "click" || !targetEl) return;
    let fired = false; // Doppelklick darf nicht zwei Schritte überspringen
    const onClick = (event: MouseEvent) => {
      const el = targetRef.current;
      if (!fired && el && event.target instanceof Node && el.contains(event.target)) {
        fired = true;
        // Erst die App reagieren lassen (Dialog öffnen, navigieren …), dann weiter.
        window.setTimeout(goNext, 350);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, index, targetEl]);

  // Escape bricht ab.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onFinish(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFinish]);

  if (!step) return null;
  if (!ready) {
    // Kurzer Hinweis, solange auf das nächste Element gewartet wird.
    return createPortal(
      <div data-guided-tour className="pointer-events-none fixed inset-x-0 bottom-6 z-[95] flex justify-center">
        <div className="rounded-full border border-[var(--line)] bg-white px-4 py-2 text-xs font-semibold text-[var(--ink-mid)] shadow-[0_12px_30px_rgba(16,32,38,.18)]">
          ⏳ Einen Moment … <span className="font-normal text-[var(--ink-muted)]">(Esc beendet das Tutorial)</span>
        </div>
      </div>,
      document.body
    );
  }

  const back = () => setIndex((value) => Math.max(0, value - 1));

  // Tooltip-Position: unter dem Ziel, sonst darüber (an der Unterkante verankert, damit
  // ein langer Text nie über das Ziel wächst), sonst daneben; immer im Viewport.
  let tooltipStyle: CSSProperties;
  if (rect) {
    const estHeight = 230;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const spaceBelow = vh - (rect.top + rect.height);
    const spaceAbove = rect.top;
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(12, Math.min(left, vw - TOOLTIP_WIDTH - 12));
    if (spaceBelow >= estHeight + 24 || spaceBelow >= spaceAbove) {
      const top = rect.top + rect.height + 14;
      tooltipStyle = { position: "fixed", top, left, width: TOOLTIP_WIDTH, maxHeight: Math.max(160, vh - top - 12), overflowY: "auto" };
    } else {
      tooltipStyle = { position: "fixed", bottom: vh - rect.top + 14, left, width: TOOLTIP_WIDTH, maxHeight: Math.max(160, rect.top - 26), overflowY: "auto" };
    }
    if (spaceBelow < 180 && spaceAbove < 180) {
      // Großes Ziel ohne Platz ober-/unterhalb: rechts bzw. links daneben.
      const sideLeft = rect.left + rect.width + 14 + TOOLTIP_WIDTH < vw ? rect.left + rect.width + 14 : rect.left - 14 - TOOLTIP_WIDTH;
      tooltipStyle = { position: "fixed", top: 12, left: Math.max(12, sideLeft), width: TOOLTIP_WIDTH, maxHeight: vh - 24, overflowY: "auto" };
    }
  } else {
    tooltipStyle = { position: "fixed", top: "50%", left: "50%", width: TOOLTIP_WIDTH, transform: "translate(-50%, -50%)" };
  }

  // Klick-Blocker: bei Mitmach-Schritten vier Flächen rund um das Ziel, sodass nur
  // das markierte Element bedienbar bleibt; sonst eine vollflächige Sperre.
  const hole = rect
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : null;
  const blockers: CSSProperties[] = interactive && hole
    ? [
        { top: 0, left: 0, right: 0, height: Math.max(0, hole.top) },
        { top: hole.top + hole.height, left: 0, right: 0, bottom: 0 },
        { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
        { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height },
      ]
    : [{ inset: 0 }];

  const canContinue = step.action !== "input" || !step.requireValue || hasValue;

  return createPortal(
    <div data-guided-tour>
      {/* Klicksperre unterhalb globaler Bestätigungsdialoge (z-90) … */}
      <div className="pointer-events-none fixed inset-0 z-[80]">
        {blockers.map((style, i) => (
          <div key={i} className="pointer-events-auto fixed" style={style} />
        ))}
        {!hole && <div className="absolute inset-0 bg-slate-900/65" />}
      </div>
      {/* … Markierung und Erklärung darüber, damit sie auch auf Dialogen sichtbar sind. */}
      <div className="pointer-events-none fixed inset-0 z-[95]">
      {hole && (
        <div
          style={{
            position: "fixed",
            ...hole,
            borderRadius: 10,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.6)",
            border: `2px solid ${step.action ? "#E9B53A" : "#009FB5"}`,
            pointerEvents: "none",
            transition: "top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease",
          }}
        />
      )}
      <div
        style={tooltipStyle}
        role="dialog"
        aria-live="polite"
        className="pointer-events-auto rounded-[14px] border border-[var(--line)] bg-white p-4 shadow-[0_18px_40px_rgba(16,32,38,.22)]"
      >
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[var(--teal)]" aria-hidden="true" />
          <div className="text-sm font-bold text-[var(--ink)]">{step.title}</div>
        </div>
        <div className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-[var(--ink-mid)]">{step.body}</div>
        {interactive && step.action === "click" && (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
            👉 Klicke jetzt auf das markierte Element.
          </div>
        )}
        {interactive && step.action === "input" && (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
            ✍️ Fülle das markierte Feld aus{step.requireValue ? "" : " (optional)"} und klicke dann auf „Weiter“.
          </div>
        )}
        {step.action && !rect && hasTarget(step) && (
          <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-[var(--ink-muted)]">
            Das Element ist gerade nicht sichtbar. Führe den Schritt selbst aus und klicke auf „Weiter“.
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={() => onFinish(false)} className="text-xs text-[var(--ink-muted)] hover:text-[var(--ink-mid)]">
            {steps.some((s) => s.action) ? "Beenden" : "Überspringen"}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs tabular-nums text-[var(--ink-muted)]">{index + 1} / {steps.length}</span>
            {index > 0 && !steps.some((s) => s.action) && (
              <Btn variant="secondary" className="py-1 text-xs" onClick={back}>Zurück</Btn>
            )}
            {!(interactive && step.action === "click") && (
              <Btn className="py-1 text-xs" onClick={goNext} disabled={!canContinue}>
                {isLast ? "Fertig" : "Weiter"}
              </Btn>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
}
